/**
 * TextArtifactPanel — view/edit/publish lifecycle for text-shaped AI artifacts
 * (study summary, recruiter script, agent brief). Shared by OverviewTab,
 * ScriptTab and AgentBriefTab.
 *
 * Two strictly separated surfaces (mirroring PrescreenTab):
 *   - The PUBLISHED view: the latest status='published' artifact. This is the
 *     ONLY content recruiters ever see — an unreviewed draft must never read
 *     as the live summary/script, and regenerating must never hide the
 *     reviewed version.
 *   - The admin DRAFT workspace below it: generating spinner / failed error /
 *     editable draft with Save + Publish. Regenerate is always available
 *     (it version-bumps past any abandoned draft).
 */
import { useEffect, useRef, useState } from 'react';
import { Loader2, Pencil, Sparkles, CheckCircle2 } from 'lucide-react';
import api from '../../../services/api';
import { ArtifactType, StudyArtifact, StudyDetailResponse } from '../../types';
import { relTime, studyHasExtractedDoc } from '../../lib';
import { useConfirm } from '../ui';
import MarkdownText from '../MarkdownText';
import { VersionHistoryButton } from '../versions';

interface TextArtifactPanelProps {
  detail: StudyDetailResponse;
  artifactType: Extract<ArtifactType, 'summary' | 'recruiter_script' | 'agent_brief'>;
  title: string;
  emptyHint: string;
  isAdmin: boolean;
  onChanged: () => void;
}

/** Latest artifact of a type regardless of status (artifacts arrive version-desc). */
export function latestArtifact(artifacts: StudyArtifact[], type: ArtifactType): StudyArtifact | null {
  return artifacts.find((a) => a.artifact_type === type) || null;
}

/** Latest PUBLISHED artifact of a type — the recruiter-facing content. */
export function publishedArtifact(artifacts: StudyArtifact[], type: ArtifactType): StudyArtifact | null {
  return artifacts.find((a) => a.artifact_type === type && a.status === 'published') || null;
}

/**
 * The latest artifact of a type ONLY while it is an in-progress "working" state
 * (generating / draft / failed) — else null. This is the AI-draft nudge surface;
 * once the latest is settled (published, or dismissed → superseded) there is no
 * nudge, so the AI stops offering to replace the finalized content.
 */
export function workingArtifact(artifacts: StudyArtifact[], type: ArtifactType): StudyArtifact | null {
  const latest = latestArtifact(artifacts, type);
  return latest && ['generating', 'draft', 'failed'].includes(latest.status) ? latest : null;
}

export default function TextArtifactPanel({ detail, artifactType, title, emptyHint, isAdmin, onChanged }: TextArtifactPanelProps) {
  const confirm = useConfirm();
  const published = publishedArtifact(detail.artifacts, artifactType);
  const latest = latestArtifact(detail.artifacts, artifactType);
  // The draft workspace shows the latest artifact only while it is in an
  // unpublished lifecycle state (a published latest IS the published view).
  const working = latest && ['generating', 'draft', 'failed'].includes(latest.status) ? latest : null;

  const [editing, setEditing] = useState(false);
  const [draftText, setDraftText] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Set right before a "Edit published" fork so the freshly-arrived draft opens
  // straight into the editor (seeded with the copied text) — no second click.
  const autoEditRef = useRef(false);

  // A fresh artifact (regenerate/poll flip) discards stale local edit state —
  // unless we just forked the published version to hand-edit it, in which case
  // open the editor on the new draft immediately.
  useEffect(() => {
    if (autoEditRef.current && working?.status === 'draft') {
      autoEditRef.current = false;
      setDraftText(working.content?.text || '');
      setEditing(true);
    } else {
      setEditing(false);
    }
    setError(null);
    // Seed only on a new draft (id/status) — not on every detail poll, which
    // would clobber in-progress edits. content is read once at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [working?.id, working?.status]);

  const hasExtractedDoc = studyHasExtractedDoc(detail);

  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    setError(null);
    try {
      await fn();
      // Await the reload so `busy` stays set until fresh detail lands — this
      // keeps the action buttons disabled through the refetch gap (otherwise a
      // fast double-click after a fork/publish re-fires before `working` flips).
      await onChanged();
    } catch (err: any) {
      setError(err?.response?.data?.error || `${label} failed`);
    } finally {
      setBusy(null);
    }
  };

  const generate = async () => {
    if (working?.status === 'draft' && !(await confirm({
      title: 'Discard the current draft and generate a new one?',
      confirmLabel: 'Discard and regenerate',
      danger: true,
    }))) return;
    run('Generate', () => api.post(`/api/crm/studies/${detail.study.id}/generate`, { types: [artifactType] }));
  };
  const save = () => run('Save', async () => {
    await api.patch(`/api/crm/artifacts/${working!.id}`, { content: { text: draftText } });
    setEditing(false);
  });
  const publish = () => run('Publish', () => api.post(`/api/crm/artifacts/${working!.id}/publish`, {}));
  // Set the AI draft aside so it stops re-appearing on every visit. The published
  // version stays live; the AI only returns on an explicit Regenerate.
  const discard = async () => {
    if (working!.status === 'draft'
        && !(await confirm({
          title: 'Dismiss this AI draft?',
          message: 'Your published version stays as-is — you can regenerate later.',
          confirmLabel: 'Dismiss draft',
          danger: true,
        }))) return;
    run('Discard', () => api.post(`/api/crm/artifacts/${working!.id}/discard`, {}));
  };
  // Hand-edit the live version: fork it into a new draft (no AI) and open the editor.
  const editPublished = () => run('Edit', async () => {
    autoEditRef.current = true;
    try {
      await api.post(`/api/crm/artifacts/${published!.id}/revise`, {});
    } catch (e) {
      autoEditRef.current = false; // don't auto-open a later draft if the fork failed
      throw e;
    }
  });

  const generateBtn = (label: string) => (
    <button
      type="button"
      onClick={generate}
      disabled={busy !== null || !hasExtractedDoc || working?.status === 'generating'}
      title={!hasExtractedDoc ? 'Upload a protocol/ICF in Documents first' : undefined}
      className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <Sparkles className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <div className="space-y-4">
      {/* ---- Published view (the only recruiter-facing content) ---- */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-6">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
            {published && (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/40 dark:text-green-300">
                <CheckCircle2 className="h-3 w-3" /> Published v{published.version}
              </span>
            )}
            {/* Every version of this artifact (published + superseded drafts):
                diff any two + who did what. Self-hides for non-CRM-admins. */}
            {latest && (
              <VersionHistoryButton
                kind="study_artifact"
                params={{ study_id: detail.study.id, artifact_type: artifactType }}
                title={title}
              />
            )}
          </div>
          {isAdmin && !working && (
            <div className="flex items-center gap-2">
              {published && (
                <button
                  type="button"
                  onClick={editPublished}
                  disabled={busy !== null}
                  title="Edit the published version by hand (no AI) and publish it as a new version"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  <Pencil className="h-3.5 w-3.5" /> {busy === 'Edit' ? 'Opening…' : 'Edit'}
                </button>
              )}
              {generateBtn(published ? 'Regenerate' : 'Generate with AI')}
            </div>
          )}
        </div>

        {published ? (
          <>
            <MarkdownText text={published.content?.text || ''} />
            <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
              Published {relTime(published.published_at || published.created_at)} ago
            </p>
          </>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">{emptyHint}</p>
        )}
      </div>

      {/* ---- Admin draft workspace ---- */}
      {isAdmin && working && (
        <div className="rounded-xl border border-amber-200 bg-white p-4 dark:border-amber-800/60 dark:bg-gray-800 sm:p-6">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Draft v{working.version}</h3>
              {working.generation_options?.source_update ? (
                <span
                  className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/40 dark:text-primary-300"
                  title="Built from your previous version by applying only the changes in the new eReg document version"
                >
                  {working.generation_options.source_update.outcome === 'no_changes'
                    ? 'New eReg version — no text changes'
                    : `Updated from new eReg version${working.generation_options.revised_from != null ? ` (from v${working.generation_options.revised_from})` : ''}`}
                </span>
              ) : working.generation_options?.revised_from != null && (
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  Copied from v{working.generation_options.revised_from}
                </span>
              )}
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                Not visible to recruiters until published
              </span>
            </div>
            <div className="flex items-center gap-2">
              {working.status === 'draft' && !editing && (
                <>
                  <button
                    type="button"
                    onClick={() => { setDraftText(working.content?.text || ''); setEditing(true); }}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  <button
                    type="button"
                    onClick={publish}
                    disabled={busy !== null}
                    className="rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:opacity-50"
                  >
                    {busy === 'Publish' ? 'Publishing…' : 'Publish'}
                  </button>
                </>
              )}
              {working.status !== 'generating' && (
                <button
                  type="button"
                  onClick={discard}
                  disabled={busy !== null}
                  title="Dismiss this AI draft and keep the published version"
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  {busy === 'Discard' ? 'Dismissing…' : 'Dismiss'}
                </button>
              )}
              {working.status !== 'generating' && generateBtn(working.status === 'failed' ? 'Try again' : 'Regenerate')}
            </div>
          </div>

          {error && <p className="mb-3 text-sm text-red-600 dark:text-red-400">{error}</p>}

          {working.status === 'generating' && (
            <div className="flex items-center gap-3 rounded-lg bg-primary-50 p-4 dark:bg-primary-900/20">
              <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
              <p className="text-sm text-primary-800 dark:text-primary-200">
                {working.generation_options?.source_update
                  ? 'Applying the new document version to your current text — only the passages that changed are being revised…'
                  : 'Generating from the study document — this can take a minute…'}
              </p>
            </div>
          )}

          {working.status === 'failed' && (
            <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
              Generation failed: {working.error || 'unknown error'}
            </p>
          )}

          {working.status === 'draft' && (
            editing ? (
              <div>
                <textarea
                  rows={18}
                  value={draftText}
                  onChange={(e) => setDraftText(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 font-mono text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                />
                <div className="mt-2 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setEditing(false)}
                    className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={save}
                    disabled={busy !== null || !draftText.trim()}
                    className="rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:opacity-50"
                  >
                    {busy === 'Save' ? 'Saving…' : 'Save draft'}
                  </button>
                </div>
              </div>
            ) : (
              <MarkdownText text={working.content?.text || ''} />
            )
          )}

          {working.generated_at && (
            <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">Generated {relTime(working.generated_at)} ago</p>
          )}
        </div>
      )}
    </div>
  );
}
